/**
 * ============================================================
 * COLLECTION CIRCLES SLIDER — collection-circles.js
 * File: assets/collection-circles.js
 * v4 — arrows toggle + dots / bar progress indicator
 * ============================================================
 * Plain vanilla JS IIFE — no dependencies, no custom elements.
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Utility ─────────────────────────────────────────────── */

  function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  /* ── Slider ───────────────────────────────────────────────── */

  function CollectionCircles(section) {
    this.section      = section;
    this.track        = section.querySelector('[data-track]');
    this.clip         = section.querySelector('[data-track-clip]');
    this.prevBtn      = section.querySelector('[data-prev]');
    this.nextBtn      = section.querySelector('[data-next]');
    this.items        = Array.from(section.querySelectorAll('.cc-item'));
    this.progressType = section.dataset.progressType || 'none';

    /* Progress: dots */
    this.dots         = Array.from(section.querySelectorAll('[data-dot]'));

    /* Progress: bar */
    this.barFill      = section.querySelector('[data-bar-fill]');

    if (!this.track || this.items.length === 0) return;

    this.offset          = 0;
    this.maxOffset       = 0;
    this.isDragging      = false;
    this.dragStartX      = 0;
    this.dragStartOffset = 0;

    this._measure();
    this._applyOffset(0, false);
    this._bindEvents();
  }

  /* ── Measure max scroll ─────────────────────────────────── */

  CollectionCircles.prototype._measure = function () {
    var clipW  = this.clip.offsetWidth;
    var trackW = this.track.scrollWidth;
    this.maxOffset = Math.min(0, clipW - trackW);

    /*
      Center circles when they don't fill the full width.
      Remove centering when they overflow so translateX scrolling
      is calculated from the left edge (not a centered position).
    */
    if (this.maxOffset === 0) {
      this.clip.classList.add('cc-track-clip--centered');
    } else {
      this.clip.classList.remove('cc-track-clip--centered');
    }
  };

  /* ── Apply translateX offset ────────────────────────────── */

  CollectionCircles.prototype._applyOffset = function (offset, animate) {
    this.offset = clamp(offset, this.maxOffset, 0);

    this.track.style.transition = animate
      ? 'transform 0.42s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      : 'none';
    this.track.style.transform = 'translateX(' + this.offset + 'px)';

    this._updateArrows();
    this._updateProgress();
  };

  /* ── Arrow disabled states ──────────────────────────────── */

  CollectionCircles.prototype._updateArrows = function () {
    if (this.prevBtn) {
      if (this.offset >= 0) {
        this.prevBtn.setAttribute('disabled', '');
      } else {
        this.prevBtn.removeAttribute('disabled');
      }
    }
    if (this.nextBtn) {
      if (this.offset <= this.maxOffset) {
        this.nextBtn.setAttribute('disabled', '');
      } else {
        this.nextBtn.removeAttribute('disabled');
      }
    }
  };

  /* ── Update progress indicator ──────────────────────────── */

  CollectionCircles.prototype._updateProgress = function () {
    if (this.progressType === 'none') return;

    /* scrollRatio: 0 = at start, 1 = at end */
    var scrollRatio = this.maxOffset === 0
      ? 0
      : this.offset / this.maxOffset;  /* both negative → positive ratio */

    /* ── BAR ─────────────────────────────────────────────── */
    if (this.progressType === 'bar' && this.barFill) {
      var pct = Math.round(scrollRatio * 100);
      this.barFill.style.width = pct + '%';
      return;
    }

    /* ── DOTS ─────────────────────────────────────────────── */
    if (this.progressType === 'dots' && this.dots.length > 0) {
      /*
        Determine which item is closest to the left edge of the clip.
        We compare each item's left position against the current offset.
      */
      var clipLeft   = this.clip.getBoundingClientRect().left;
      var activeIdx  = 0;
      var minDist    = Infinity;

      for (var i = 0; i < this.items.length; i++) {
        var itemLeft = this.items[i].getBoundingClientRect().left;
        var dist     = Math.abs(itemLeft - clipLeft);
        if (dist < minDist) {
          minDist   = dist;
          activeIdx = i;
        }
      }

      /* Update dot classes and aria-selected */
      for (var j = 0; j < this.dots.length; j++) {
        if (j === activeIdx) {
          this.dots[j].classList.add('is-active');
          this.dots[j].setAttribute('aria-selected', 'true');
        } else {
          this.dots[j].classList.remove('is-active');
          this.dots[j].setAttribute('aria-selected', 'false');
        }
      }
    }
  };

  /* ── Step size (one item + gap) ─────────────────────────── */

  CollectionCircles.prototype._step = function () {
    var first = this.items[0];
    if (!first) return 200;
    var gap = parseFloat(window.getComputedStyle(this.track).gap) || 0;
    return first.offsetWidth + gap;
  };

  /* ── Scroll by N items ──────────────────────────────────── */

  CollectionCircles.prototype.scroll = function (direction) {
    this._measure();
    var step = this._step() * 2;
    var next = this.offset + (direction === 'prev' ? step : -step);
    this._applyOffset(next, true);
  };

  /* ── Scroll to a specific item (used by dot clicks) ─────── */

  CollectionCircles.prototype.scrollToItem = function (index) {
    this._measure();
    var item = this.items[index];
    if (!item) return;

    /*
      Item's left offset relative to the track's left edge.
      offsetLeft gives position within the scrollable track.
    */
    var targetOffset = -item.offsetLeft;
    this._applyOffset(targetOffset, true);
  };

  /* ── Event bindings ─────────────────────────────────────── */

  CollectionCircles.prototype._bindEvents = function () {
    var self = this;

    /* Arrow buttons */
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () { self.scroll('prev'); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () { self.scroll('next'); });
    }

    /* Dot buttons */
    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.dot, 10);
        self.scrollToItem(idx);
      });
    });

    /* Keyboard arrow keys */
    this.section.setAttribute('tabindex', '0');
    this.section.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); self.scroll('prev'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.scroll('next'); }
    });

    /* ── Touch drag ──────────────────────────────────────── */
    var touchStartX      = 0;
    var touchStartOffset = 0;

    this.clip.addEventListener('touchstart', function (e) {
      touchStartX      = e.touches[0].clientX;
      touchStartOffset = self.offset;
      self.track.style.transition = 'none';
    }, { passive: true });

    this.clip.addEventListener('touchmove', function (e) {
      var dx = e.touches[0].clientX - touchStartX;
      self._applyOffset(touchStartOffset + dx, false);
    }, { passive: true });

    this.clip.addEventListener('touchend', function (e) {
      var dx   = e.changedTouches[0].clientX - touchStartX;
      var step = self._step();
      if (Math.abs(dx) > step * 0.2) {
        /* Snap to nearest item boundary */
        var snapped = Math.round(self.offset / step) * step;
        self._applyOffset(snapped, true);
      } else {
        self._applyOffset(self.offset, true);
      }
    }, { passive: true });

    /* ── Mouse drag ──────────────────────────────────────── */
    this.clip.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      self.isDragging      = true;
      self.dragStartX      = e.clientX;
      self.dragStartOffset = self.offset;
      self.clip.style.cursor = 'grabbing';
      self.track.style.transition = 'none';
      e.preventDefault();
    });

    document.addEventListener('mousemove', function (e) {
      if (!self.isDragging) return;
      var dx = e.clientX - self.dragStartX;
      self._applyOffset(self.dragStartOffset + dx, false);
    });

    document.addEventListener('mouseup', function (e) {
      if (!self.isDragging) return;
      self.isDragging        = false;
      self.clip.style.cursor = '';
      var dx   = e.clientX - self.dragStartX;
      var step = self._step();
      if (Math.abs(dx) > step * 0.2) {
        var snapped = Math.round(self.offset / step) * step;
        self._applyOffset(snapped, true);
      } else {
        self._applyOffset(self.offset, true);
      }
    });

    /* Prevent link click if user was actually dragging */
    this.clip.addEventListener('click', function (e) {
      if (Math.abs(self.offset - self.dragStartOffset) > 8) {
        e.preventDefault();
      }
    }, true);

    /* ── Resize: recalculate on viewport change ──────────── */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () {
        self._measure();
        self._applyOffset(self.offset, false);
      });
      ro.observe(this.clip);
    } else {
      window.addEventListener('resize', function () {
        self._measure();
        self._applyOffset(self.offset, false);
      });
    }
  };

  /* ── Bootstrap ───────────────────────────────────────────── */

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-slider]').forEach(function (sliderEl) {
      var section = sliderEl.closest('[data-section-id]') || sliderEl.parentElement;
      if (!section || section.dataset.ccReady) return;
      section.dataset.ccReady = 'true';
      new CollectionCircles(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* ── Shopify Theme Editor ────────────────────────────────── */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-section-id]');
    if (!section) return;
    delete section.dataset.ccReady;
    initAll(e.target);
  });

})();
